<script>
import {useRoute, useRouter} from "vue-router";
import {useDonorStore} from "@/javascript/stores/donor";
import {usePossibleDuplicateStore} from "@/javascript/stores/possibleDuplicate";
import {useOrganizationStore} from "@/javascript/stores/organizations";
import Index from "@/javascript/views/components/dashboard/Index.vue";
import Show from "@/javascript/views/components/dashboard/Show.vue";
import Popup from '../../components/popup.vue';
import TabsComponent from "@/javascript/views/components/dashboard/Tabs.vue";
import HeaderBar from "@/javascript/views/components/HeaderBar.vue";
import {useAnalyticEventStore} from "../../../stores/analyticEvents";
import LoadingSpinner from "../../components/loadingSpinner.vue";
import Icon from "../../components/Icon.vue";
import DropdownMenuButton from "../../components/dropdownMenuButton.vue";
import DuplicateDonorsList from "./DuplicateDonorsList.vue";
import DuplicateDonorReview from "./DuplicateDonorReview.vue";

export default {
  components: {DropdownMenuButton, Icon, LoadingSpinner, HeaderBar, Index, Show, TabsComponent, Popup, DuplicateDonorsList, DuplicateDonorReview},
  setup() {
    const route = useRoute();
    const router = useRouter();
    const donorStore = useDonorStore();
    const possibleDuplicateStore = usePossibleDuplicateStore();
    const organizationStore = useOrganizationStore();
    const analyticEventStore = useAnalyticEventStore();

    const recordAnalyticEvent = (action) => {
      analyticEventStore.setOrganizationId(organizationStore.organizationId);
      analyticEventStore.logEvent({action});
    };

    return {
      route,
      router,
      donorStore,
      possibleDuplicateStore,
      organizationStore,
      analyticEventStore,
    };
  },
  data() {
    return {
      // Flag to enable tabs. If only one tab is present, the tabs component will hide the header.
      hasTabs: true,
      downloading: false,
      showReportModal: true,
      popupMessage: ""
    };
  },
  computed: {
    donorId() {
      return this.route.params.id;
    },
    activePanel() {
      return this.route.params.panel || "donors";
    },
    onDuplicatesTab() {
      return this.activePanel === "duplicate-donors";
    },
    activeTab() {
      return this.onDuplicatesTab ? "duplicates-tab" : "donors-tab";
    },
    tabs() {
      const dupTab = {id: "duplicates-tab", label: "Duplicate Donors"};
      if (this.possibleDuplicateStore.hasPendingDuplicates) {
        dupTab.icon = "warning-outline";
        dupTab.iconColor = "#EAB308"; // yellow-500
      }
      return [
        {id: "donors-tab", label: "Donors"},
        dupTab
      ];
    }
  },
  watch: {
    'donorStore.organizationId'() {
      this.donorStore.loadData();
    },
    'organizationStore.organizationId'(id) {
      if (id) {
        this.possibleDuplicateStore.setOrganizationId(id);
        this.possibleDuplicateStore.loadReviews();
      }
    },
    'possibleDuplicateStore.successMessage'(message) {
      if (!message) return;
      this.showPopup(message);
      //calling the method to clear the donor search
      this.donorStore.clearSearch();
      this.possibleDuplicateStore.successMessage = "";
    }
  },
  mounted() {
    this.donorStore.loadData();
    this.possibleDuplicateStore.setOrganizationId(this.organizationStore.organizationId);
    this.possibleDuplicateStore.loadReviews();
  },
  methods: {
    recordAnalyticEvent(action) {
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({
        action: action
      });
    },
    resendInvitationEmail(){
      this.donorStore.resendInvitationEmail(this.donorStore.currentRecord.user_id);
      this.showPopup("An email has been sent!");
    },
    showPopup(message) {
      this.popupMessage = message;
      this.$refs.popup.show();
    },
    switchTab(tab) {
      const panel = tab === "duplicates-tab" ? "duplicate-donors" : "donors";
      const params = {...this.route.params, panel};
      delete params.id; // switching tabs returns to the list, not a drilldown
      const routeName = this.route.name || "client-dashboard";
      this.router.replace({name: routeName, params});
    },
  }
};
</script>

<template>
  <div>
    <!-- Show detailed view if a donor is selected -->
    <div v-if="donorId && !onDuplicatesTab && donorStore.currentRecord">
      <show
        :model-store="donorStore"
        editable
      >
        <template
          v-if="donorStore.currentRecord.user_details?.role === 'No Login'"
          #button-slot
        >
          <button
            class="btn-filled"
            @click="resendInvitationEmail"
          >
            Resend Invitation Email
          </button>
        </template>
      </show>
    </div>
    <!-- Otherwise show list view -->
    <div v-else>
      <!-- Donors page header: displayed above tabs and table -->
      <div class="px-4">
        <header-bar
          title="Donors"
        />
      </div>
      <!-- If tabs are enabled, wrap the tabs component in a container that shares the same max-width -->
      <div
        v-if="hasTabs"
        class="mt-2 pt-2 w-full p-4 mx-auto"
      >
        <div class="breakpoint-container relative rounded-xl overflow-hidden border border-gray-300 text-xs">
          <tabs-component
            v-model="activeTab"
            :tabs="tabs"
            @tab-changed="switchTab"
          >
            <!-- Donors tab: Wrap the Index component -->
            <template #donors-tab>
              <index :model-store="donorStore" />
            </template>
            <template #duplicates-tab>
              <duplicate-donor-review v-if="donorId" />
              <duplicate-donors-list v-else />
            </template>
          </tabs-component>
        </div>
      </div>
      <!-- If tabs are not enabled, show the Index component with same max-width -->
      <div
        v-else
        class="breakpoint-container mt-4"
      >
        <index :model-store="donorStore" />
      </div>
    </div>
    <!-- Popup Component -->
    <popup
      ref="popup"
      :message="popupMessage"
      :duration="1400"
    />
  </div>
</template>
